import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import StarRating from '../components/StarRating';
import ReportModal from '../components/ReportModal';
import useAuthStore from '../store/useAuthStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import useToastStore from '../store/useToastStore';
import api from '../services/api';

const handleShare = (title, url) => {
  if (navigator.share) {
    navigator.share({ title, url }).catch(() => {});
  } else {
    navigator.clipboard.writeText(url);
    useToastStore.getState().addToast('Link copied!', 'success');
  }
};

export default function ProviderProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currency } = useCurrencyStore();
  const [provider, setProvider] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services');

  useEffect(() => {
    api.get(`/users/${id}`)
      .then(r => { setProvider(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!provider) return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center text-dark-subtext">
      Provider not found
    </div>
  );

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-12 left-4 z-10 bg-dark-surface/80 backdrop-blur p-2 rounded-full"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EBEBF5" strokeWidth="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      {/* Cover/banner */}
      <div className="h-40 relative overflow-hidden">
        {provider.bannerUrl ? (
          <img src={provider.bannerUrl} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #6C5CE7 0%, #a855f7 50%, #ec4899 100%)' }} />
        )}
      </div>

      {/* Profile info */}
      <div className="px-4 -mt-16 mb-6 relative z-10">
        <div className="flex items-end justify-between mb-4">
          <div className="w-24 h-24 rounded-2xl border-4 border-dark-bg overflow-hidden bg-brand-gradient flex items-center justify-center">
            {provider.avatar ? (
              <img src={provider.avatar} alt={provider.fullName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-3xl font-bold">{provider.fullName[0]}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleShare(provider.fullName, window.location.href)}
              className="p-2 rounded-xl bg-dark-surface border border-dark-border text-dark-subtext"
              aria-label="Share profile"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
            <button
              onClick={() => navigate(`/inbox?provider=${id}`)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-surface border border-dark-border text-dark-text text-sm font-medium"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              Message
            </button>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-dark-text mb-0.5">{provider.fullName}</h1>
        {provider.companyName && (
          <p className="text-brand-purple text-sm font-medium mb-1">{provider.companyName}</p>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <StarRating rating={provider.rating || 0} size={14} />
            <span className="text-dark-text text-sm font-semibold">{(provider.rating || 0).toFixed(1)}</span>
            <span className="text-dark-subtext text-xs">({provider.reviewCount || 0})</span>
          </div>
          <span className="text-dark-border">|</span>
          <span className="text-dark-subtext text-sm">{provider.services?.length || 0} services</span>
          {provider.hourlyRate && (
            <>
              <span className="text-dark-border">|</span>
              <span className="text-brand-purple text-sm font-semibold">{formatPrice(provider.hourlyRate, currency)}/hr</span>
            </>
          )}
        </div>

        {/* Response time & member badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            Fast Response
          </span>
          {provider.isVerified && (
            <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Verified
            </span>
          )}
          {provider.createdAt && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-dark-surface border border-dark-border text-dark-subtext">
              Joined {new Date(provider.createdAt).toLocaleDateString('en', { month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>

        {provider.bio && (
          <p className="text-dark-subtext text-sm leading-relaxed mb-3">{provider.bio}</p>
        )}

        {provider.portfolioUrl && (
          <a
            href={provider.portfolioUrl.startsWith('http') ? provider.portfolioUrl : `https://${provider.portfolioUrl}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-brand-purple text-xs font-medium mb-3 hover:underline"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            View Portfolio
          </a>
        )}

        {/* Skills */}
        {provider.skills?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {provider.skills.map(s => (
              <span key={s} className="text-xs px-3 py-1 rounded-full bg-brand-purple/10 text-brand-purple border border-brand-purple/20">
                {s}
              </span>
            ))}
          </div>
        )}

        {/* Hire button */}
        <button
          onClick={() => provider.services?.[0] && navigate(`/service/${provider.services[0].id}`)}
          className="w-full py-4 rounded-2xl bg-brand-gradient text-white font-semibold text-base glow"
        >
          Hire {provider.fullName.split(' ')[0]}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-dark-border mb-4 px-4">
        {['services', 'reviews'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-brand-purple border-b-2 border-brand-purple'
                : 'text-dark-subtext'
            }`}
          >
            {tab} {tab === 'reviews' && `(${provider.reviewCount || 0})`}
          </button>
        ))}
      </div>

      <div className="px-4">
        {activeTab === 'services' && (
          <div className="grid grid-cols-2 gap-3">
            {provider.services?.map(s => <ServiceCard key={s.id} service={{ ...s, provider }} />)}
          </div>
        )}
        {activeTab === 'reviews' && (
          <div className="flex flex-col gap-4">
            {provider.receivedReviews?.length === 0 ? (
              <p className="text-center text-dark-subtext py-8">No reviews yet</p>
            ) : provider.receivedReviews?.map(r => (
              <div key={r.id} className="bg-dark-surface border border-dark-border rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-brand-gradient flex items-center justify-center text-white text-sm font-bold">
                    {r.reviewer.fullName[0]}
                  </div>
                  <div>
                    <p className="text-dark-text text-sm font-medium">{r.reviewer.fullName}</p>
                    <StarRating rating={r.rating} size={12} />
                  </div>
                </div>
                {r.comment && <p className="text-dark-subtext text-sm">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Report link */}
        {user?.id !== id && (
          <div className="text-center py-4">
            <button onClick={() => setShowReport(true)} className="text-dark-subtext text-xs underline underline-offset-2">
              Report this profile
            </button>
          </div>
        )}
      </div>

      {showReport && (
        <ReportModal
          targetType="USER"
          targetId={id}
          targetName={provider?.fullName}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}
